// Step indicator component.
//
// Shows progress through a multi-step workflow with numbered steps and
// connecting lines.
//

import { Check } from "lucide-react";

interface Step {
  id: number;
  label: string;
}

interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
  isComplete?: boolean;
  className?: string;
}

// Step indicator showing progress through workflow.
//
export function StepIndicator({
  steps,
  currentStep,
  isComplete = false,
  className = "",
}: StepIndicatorProps) {
  return (
    <div className={`flex items-center justify-center ${className}`}>
      {steps.map((step, index) => {
        const isCompleted =
          step.id < currentStep || (step.id === currentStep && isComplete);
        const isCurrent = step.id === currentStep && !isComplete;
        const isLast = index === steps.length - 1;

        return (
          <div key={step.id} className="flex items-center">
            {/* Step circle */}
            <div className="flex flex-col items-center">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors ${isCompleted ? "bg-green-600 text-white" : ""} ${isCurrent ? "bg-primary text-primary-foreground" : ""} ${!isCompleted && !isCurrent ? "bg-accent text-muted-foreground" : ""} `}
              >
                {isCompleted ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <span>{step.id}</span>
                )}
              </div>

              {/* Step label (hidden on small screens) */}
              <span
                className={`mt-1 hidden text-xs sm:block ${isCurrent ? "text-primary font-medium" : ""} ${isCompleted ? "text-green-600 dark:text-green-400" : ""} ${!isCompleted && !isCurrent ? "text-muted-foreground" : ""} `}
              >
                {step.label}
              </span>
            </div>

            {/* Connector line */}
            {!isLast && (
              <div
                className={`mx-2 h-0.5 w-8 sm:w-12 ${isCompleted ? "bg-green-600" : "bg-border"} `}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
